import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router";
import { RouteFactory } from "@app/routePaths";
import useUserInfoStore, {
  UserStore,
} from "@features/mySph/store/useUserInfoStore";
import { DEFAULT_USER_INFO } from "@features/mySph/utils/constants";
import { getSanitizedUserInfoObject } from "@features/mySph/utils/helpers";
import {
  FeedSSOUserDetailsResponse,
  SanitizedUserInfoObject,
} from "@features/mySph/utils/types";
import useIsAppWebView from "@hooks/useIsAppWebView";
import { useMySPHLightbox } from "@sphtech/idpf-lightbox-react";
import { tryCatch } from "@typings/TryCatch";
import axios from "axios";
import Cookies from "js-cookie";

export const useSetUserInfo = () => {
  const { pathname } = useLocation();
  const isAppWebView = useIsAppWebView();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { userInfo, isAuthenticated } = useMySPHLightbox();
  const { setUserInfo, setIsAuthenticated } = useUserInfoStore(
    (state: UserStore) => ({
      setUserInfo: state.setUserInfo,
      setIsAuthenticated: state.setIsAuthenticated,
    })
  );

  const getUserInfoFromCookieSSO = useCallback(
    async (cookieSSO: string) => {
      const beData = await getUserInfoFromBE(cookieSSO || "");
      if (!beData) return;

      setUserInfo(beData);
      setIsAuthenticated(true);
    },
    [setUserInfo, setIsAuthenticated]
  );

  const getUserInfoFromLightbox = useCallback(async () => {
    if (!userInfo) {
      setUserInfo(DEFAULT_USER_INFO);
      setIsAuthenticated(false);

      return;
    }
    const sanitizedUserInfo = getSanitizedUserInfoObject(userInfo);
    setUserInfo(sanitizedUserInfo);
    setIsAuthenticated(isAuthenticated);
  }, [setUserInfo, setIsAuthenticated, userInfo, isAuthenticated]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (pathname === RouteFactory.oAuthCallback) return;

    setIsLoading(true);
    const cookieSSO = Cookies.get("sph-auth-jwt");

    const fetchUserData = async () => {
      try {
        if (cookieSSO) {
          await getUserInfoFromCookieSSO(cookieSSO);

          return;
        }

        if (isAppWebView) return;

        await getUserInfoFromLightbox();
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [
    getUserInfoFromCookieSSO,
    getUserInfoFromLightbox,
    pathname,
    isAppWebView,
  ]);

  return { isLoading };
};

const getUserInfoFromBE = async (
  cookieSSO: string
): Promise<SanitizedUserInfoObject | undefined> => {
  const request = axios.post<FeedSSOUserDetailsResponse>(
    `/_plat/api/v1/user/auth/validate`,
    {},
    {
      headers: {
        Authorization: cookieSSO,
      },
    }
  );

  const { data, error } = await tryCatch(request);
  if (error) return;

  const sanitizedUserInfo = getSanitizedUserInfoObject(data.data);

  return sanitizedUserInfo;
};
